import DataTable from '../../../../../components/Datatable';
import Loading from '../../../../../components/Loading';
import { COLUMNS } from './keys';
import { getSortKeys, setAdditionalStyles } from '../../../../../helpers/table';
import { query } from './query';
import { Component } from 'react';
import { v4 as uuid } from 'uuid';
import { withTranslation } from 'react-i18next';
import { normalizeDomain } from '../../../../../helpers/normalizeDomain';
import AdditionalBox, { ALL_BRANDS, PROJECT_BRANDS } from './additionalBox';

class Facts extends Component {
    constructor() {
        super();

        this.getData = this.getData.bind(this);
        this.forceRun = this.forceRun.bind(this);
        this.handleSelectBrand = this.handleSelectBrand.bind(this);

        this.state = {
            data: [],
            loading: true,
            selectedBrand: PROJECT_BRANDS,
        };
    }

    async getData({ force = false, alreadyInQueue = false }) {
        try {
            this.query = query({
                prompt_uids: this.props.prompt_uids,
                platforms: this.props.platforms,
                base_domain: this.props.project?.own_domain,
                brand: this.props.project?.own_product_name,
            });
            this.setState({ loading: true });

            this.query.bind(this.setState.bind(this));
            const results = await this.query.update({ force, alreadyInQueue });
            const data = results?.map((x) => ({ id: uuid(), ...x }));
            this.setState({
                data,
                loading: false,
            });
        } catch (err) {
            console.log(err);
        }
    }

    forceRun() {
        this.getData({ force: true, alreadyInQueue: true });
    }

    async componentDidUpdate(prevProps) {
        if (
            !this.props.globalLoading &&
            this.props.globalLoading !== prevProps.globalLoading &&
            !this.props.prompt_uids?.length
        ) {
            this.setState({ data: [], loading: false });
        }
        if (
            JSON.stringify(prevProps.prompt_uids) !== JSON.stringify(this.props.prompt_uids) ||
            JSON.stringify(prevProps.platforms) !== JSON.stringify(this.props.platforms)
        ) {
            this.getData({});
        }

        if (this.props.updateAll !== prevProps.updateAll) {
            this.forceRun();
        }
    }

    /**
     * The brands tracked by the project: the project's own brand plus every
     * competitor and client it has, each identified by its domain (brand names
     * in the facts report are free text coming from the models, domains are not).
     */
    getProjectBrands() {
        const { project } = this.props;
        const brands = [
            { name: project?.own_product_name, domain: project?.own_domain },
            ...(project?.competitors ?? []),
            ...(project?.clients ?? []),
        ];

        return brands
            .map((item) => ({ ...item, domain: normalizeDomain(item?.domain) }))
            .filter((item) => item.domain);
    }

    handleSelectBrand(value) {
        this.setState({ selectedBrand: value });
    }

    searchFunction(data, search) {
        if (!search) return data;
        return data.filter(
            (item) =>
                item.fact?.toLowerCase().includes(search.toLowerCase()) ||
                item.brand?.toLowerCase().includes(search.toLowerCase()) ||
                item.domain?.toLowerCase().includes(search.toLowerCase()),
        );
    }

    collectData() {
        const { selectedBrand, data } = this.state;
        if (selectedBrand === ALL_BRANDS) return data;

        const projectDomains = this.getProjectBrands().map((item) => item.domain);
        // "Project brands" is the default, so fall back to every row when the
        // project has no brand domains to filter by — an empty table would just
        // look like the report returned nothing.
        if (selectedBrand === PROJECT_BRANDS && !projectDomains.length) return data;

        const domains = selectedBrand === PROJECT_BRANDS ? projectDomains : [selectedBrand];

        return data?.filter((item) => domains.includes(normalizeDomain(item.domain)));
    }

    render() {
        const { t } = this.props;
        const columns = COLUMNS(t);

        const additionalBox = () => (
            <AdditionalBox
                t={t}
                brandOptions={this.getProjectBrands().map((item) => ({
                    label: item.name ? `${item.name} (${item.domain})` : item.domain,
                    value: item.domain,
                }))}
                selectedBrand={this.state.selectedBrand}
                handleSelectBrand={this.handleSelectBrand}
            />
        );

        return (
            <div className="card table-main rounded-main ">
                {this.state.loading || this.props.loadingProjectForChildren ? (
                    <Loading padding={10} />
                ) : (
                    <DataTable
                        nodes={this.collectData() ?? []}
                        sortFns={getSortKeys(columns)}
                        searchFunction={this.searchFunction}
                        COLUMNS={columns}
                        defaultSortKey={'sumScore'}
                        header={t('ai_tracking.keyword_detail.facts.header')}
                        customEmptyMessage={t('global.no_data')}
                        forceRun={this.forceRun}
                        additionalBox={additionalBox}
                        additionalStyles={setAdditionalStyles(columns)}
                    />
                )}
            </div>
        );
    }
}

export default withTranslation()(Facts);
